import { Autocomplete, Box, Button, Chip, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useMemo } from "react";

import { monospaceStyle } from "../../../theme";
import {
  MissionCentre,
  MissionEntry,
  MissionLevel,
  MissionProvider,
  MissionQualifier,
} from "../../../types/analytics";
import { FilterValue } from "../../../types/productPlane";
import { filterValueLabel } from "../../Explore/exploreFilters";
import { TOOL_BUTTON_SX } from "../../ValueChain/ValueChainControls";
import {
  MISSION_LEVELS,
  MISSION_PROVIDERS,
  MissionsSettings,
  withCentre,
} from "../analyticsState";

// The level chips. The missions have a ring of their own; infrastructure
// rides with platform, as both stand on the ring of platform behaviours,
// and an unknown level rides with component, the finest the third ring
// holds.
export const LEVEL_CHIPS: { label: string; levels: MissionLevel[] }[] = [
  { label: "missions", levels: ["operator"] },
  { label: "platform", levels: ["platform", "infrastructure"] },
  { label: "module", levels: ["module"] },
  { label: "component", levels: ["component", "unknown"] },
];

export const PROVIDER_CHIPS: { label: string; provider: MissionProvider }[] = [
  { label: "on record inside", provider: "inside" },
  { label: "could provide", provider: "could" },
];

// The heading of a group of the picker: the mission area
const NO_AREA = "No mission area";
const areaOf = (entry: MissionEntry): string =>
  entry.area ? (entry.area.name ?? entry.area.id) : NO_AREA;

// The missions grouped by mission area: the areas by name (none last), in
// each the area itself first, then its cores by hosts
export const pickerOptions = (missions: MissionEntry[]): MissionEntry[] =>
  [...missions].sort((a, b) => {
    const areaA = areaOf(a);
    const areaB = areaOf(b);
    if (areaA !== areaB) {
      if (areaA === NO_AREA || areaB === NO_AREA) {
        return areaA === NO_AREA ? 1 : -1;
      }
      return areaA < areaB ? -1 : 1;
    }
    if (a.kind !== b.kind) {
      return a.kind === "area" ? -1 : 1;
    }
    return b.hosts - a.hosts || (a.mission.id < b.mission.id ? -1 : 1);
  });

// One row of the centre picker: a mission, or a capability of the scope's
// capability tree
export interface CentreOption {
  id: string;
  name: string;
  // The group it is listed under: "Missions", or its effect area
  group: string;
  // Products of the scope that claim it
  hosts: number | null;
  // How far beneath the top of its group it stands, for the indent
  depth: number;
  // A mission area, which stands for its cores too
  wholeArea: boolean;
}

export const MISSIONS_GROUP = "Missions";
// The group of a row of the tree with nothing beneath it and nothing above
export const OTHER_GROUP = "Other capabilities";
// The group of the centre drawn when no list holds it
const AT_CENTRE_GROUP = "At the centre";

// The most rows the picker shows at once: it draws every row it lists, and
// a scope's capability tree runs to tens of thousands of rows
export const CENTRE_ROWS_SHOWN = 200;
// With nothing typed, a longer tree is listed down to this depth: its
// effect areas and the rows directly beneath them
const BROWSE_DEPTH = 1;
// The id of the last row when more rows match than are shown
export const MORE_ROW = "more";

// The rows the picker lists for the text typed. With nothing typed, the
// whole list when it is short, else the missions and the top of the tree;
// with text, the rows whose name holds every word typed, at most
// CENTRE_ROWS_SHOWN of them in the picker's order, and a last row that says
// how many more match.
export const filterCentreOptions = (options: CentreOption[], typed: string): CentreOption[] => {
  const words = typed.toLowerCase().split(/\s+/).filter(Boolean);
  const matching =
    words.length > 0
      ? options.filter((option) => {
          const name = option.name.toLowerCase();
          return words.every((word) => name.includes(word));
        })
      : options.length <= CENTRE_ROWS_SHOWN
        ? options
        : options.filter(
            (option) =>
              option.group === MISSIONS_GROUP ||
              option.group === AT_CENTRE_GROUP ||
              option.depth <= BROWSE_DEPTH,
          );
  if (matching.length <= CENTRE_ROWS_SHOWN) {
    return matching;
  }
  const shown = matching.slice(0, CENTRE_ROWS_SHOWN);
  return [
    ...shown,
    {
      id: MORE_ROW,
      name: `${matching.length - CENTRE_ROWS_SHOWN} more match: type more of the name`,
      group: shown[shown.length - 1].group,
      hosts: null,
      depth: 0,
      wholeArea: false,
    },
  ];
};

// The rows of the centre picker: the missions as their own group (each
// area, then its cores), then the scope's capability tree by its effect
// areas, each tree row beneath its parent, the missions left out
export const centreOptions = (
  missions: MissionEntry[],
  tree: FilterValue[] | null,
): CentreOption[] => {
  const missionRows = pickerOptions(missions).map(
    (entry): CentreOption => ({
      id: entry.mission.id,
      name: entry.mission.name ?? entry.mission.id,
      group: MISSIONS_GROUP,
      hosts: entry.hosts,
      depth: entry.kind === "core" && entry.area ? 1 : 0,
      wholeArea: entry.kind === "area",
    }),
  );
  const taken = new Set(missionRows.map((row) => row.id));
  const treeRows: CentreOption[] = [];
  const lone: CentreOption[] = [];
  const walk = (value: FilterValue, group: string, depth: number, into: CentreOption[]) => {
    if (!taken.has(value.value)) {
      taken.add(value.value);
      into.push({
        id: value.value,
        name: filterValueLabel(value),
        group,
        hosts: value.count,
        depth,
        wholeArea: false,
      });
    }
    for (const child of value.children) {
      walk(child, group, depth + 1, into);
    }
  };
  for (const root of tree ?? []) {
    if (root.children.length > 0) {
      walk(root, filterValueLabel(root), 0, treeRows);
    } else {
      walk(root, OTHER_GROUP, 0, lone);
    }
  }
  return [...missionRows, ...treeRows, ...lone];
};

// A row of a qualifier picker: a condition or a mode, or none
interface QualifierOption {
  id: string | null;
  name: string;
  hosts: number | null;
}

// A chip that turns on and off, in the accent while on
const ToggleChip: React.FC<{
  label: string;
  on: boolean;
  // The last chip on of its set stays on
  locked: boolean;
  onToggle: () => void;
}> = ({ label, on, locked, onToggle }) => (
  <Chip
    label={label}
    size="small"
    variant="outlined"
    aria-pressed={on}
    disabled={on && locked}
    onClick={onToggle}
    sx={(theme) => ({
      height: 28,
      fontSize: "12.5px",
      ...(on
        ? {
            backgroundColor: alpha(theme.palette.accent.main, 0.14),
            borderColor: alpha(theme.palette.accent.main, 0.45),
            color: "text.primary",
          }
        : {}),
      // The locked chip stays legible: it is on, only not to be turned off
      "&.Mui-disabled": { opacity: 1 },
    })}
  />
);

const PICKER_SX = {
  "& .MuiOutlinedInput-root": {
    minHeight: 36,
    py: "0 !important",
    flexWrap: "nowrap",
    gap: 1,
    fontSize: 13,
    backgroundColor: "background.paper",
  },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": { py: 0, minWidth: 60 },
} as const;

// The small uppercase word at the start of a picker
const PickerHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "text.secondary",
    }}
  >
    {children}
  </Box>
);

// A row of a picker: its name, and its host count on the right
const PickerRow: React.FC<{
  props: React.HTMLAttributes<HTMLLIElement>;
  hosts: number | null;
  depth?: number;
  children: React.ReactNode;
}> = ({ props, hosts, depth = 0, children }) => {
  const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
  return (
    <Box
      component="li"
      key={key}
      {...rest}
      sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, fontSize: 13 }}
    >
      <Box component="span" sx={{ pl: depth * 1.5 }}>
        {children}
      </Box>
      {hosts !== null && (
        <Box component="span" sx={{ ...monospaceStyle, color: "text.secondary" }}>
          {hosts}
        </Box>
      )}
    </Box>
  );
};

// A picker of a condition or of a mode: "any" first, then those stated
// with the centre, each with its hosts. Off, with a short word, when none is
// stated and none is picked.
const QualifierPicker: React.FC<{
  label: string;
  any: string;
  none: string;
  rows: MissionQualifier[];
  picked: string | null;
  // The name of the pick, from the response's centre, when the rows lack it
  pickedName: string | null;
  onPick: (id: string | null) => void;
}> = ({ label, any, none, rows, picked, pickedName, onPick }) => {
  const options = useMemo((): QualifierOption[] => {
    const listed: QualifierOption[] = rows.map((row) => ({
      id: row.qualifier.id,
      name: row.qualifier.name ?? row.qualifier.id,
      hosts: row.hosts,
    }));
    if (picked && !listed.some((option) => option.id === picked)) {
      listed.unshift({ id: picked, name: pickedName ?? picked, hosts: null });
    }
    return [{ id: null, name: any, hosts: null }, ...listed];
  }, [rows, picked, pickedName, any]);
  const disabled = rows.length === 0 && !picked;
  const value = disabled ? null : (options.find((option) => option.id === picked) ?? options[0]);
  return (
    <Autocomplete<QualifierOption, false, boolean>
      size="small"
      sx={{ ...PICKER_SX, flex: "0 1 230px", minWidth: 170 }}
      options={options}
      value={value}
      disabled={disabled}
      disableClearable={!disabled}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_event, next) => {
        const id = next ? next.id : null;
        if (id !== picked) {
          onPick(id);
        }
      }}
      renderOption={(props, option) => (
        <PickerRow key={option.id ?? ""} props={props} hosts={option.hosts}>
          {option.id === null ? (
            <Box component="span" sx={{ color: "text.secondary" }}>
              {option.name}
            </Box>
          ) : (
            option.name
          )}
        </PickerRow>
      )}
      renderInput={(inputParams) => (
        <TextField
          {...inputParams}
          placeholder={disabled ? none : any}
          inputProps={{ ...inputParams.inputProps, "aria-label": label }}
          InputProps={{
            ...inputParams.InputProps,
            startAdornment: <PickerHead>{label}</PickerHead>,
          }}
        />
      )}
    />
  );
};

interface MissionsControlsProps {
  settings: MissionsSettings;
  // The missions of the scope; empty before the first response
  missions: MissionEntry[];
  // The rows of the condition and the mode pickers; empty before the first
  // response
  conditions: MissionQualifier[];
  modes: MissionQualifier[];
  // The scope's capability tree, as the product search counts it; null
  // before it is read
  tree: FilterValue[] | null;
  // Whether the tree is still being read
  treeLoading: boolean;
  centre: MissionCentre | null;
  onChange: (next: MissionsSettings) => void;
  // Whether the rings are drawn, for Fit
  drawn: boolean;
  onFit: () => void;
}

// The row above the rings: the centre picker with its condition and mode,
// "Widen to" the row above the centre, the level and provider chips, and
// Fit
const MissionsControls: React.FC<MissionsControlsProps> = ({
  settings,
  missions,
  conditions,
  modes,
  tree,
  treeLoading,
  centre,
  onChange,
  drawn,
  onFit,
}) => {
  const listed = useMemo(() => centreOptions(missions, tree), [missions, tree]);
  // The centre drawn stays named in the picker, even where no list holds it
  const options = useMemo((): CentreOption[] => {
    if (!centre || listed.some((option) => option.id === centre.capability.id)) {
      return listed;
    }
    return [
      {
        id: centre.capability.id,
        name: centre.capability.name ?? centre.capability.id,
        group: AT_CENTRE_GROUP,
        hosts: centre.hosts,
        depth: 0,
        wholeArea: false,
      },
      ...listed,
    ];
  }, [listed, centre]);
  const value = centre ? (options.find((option) => option.id === centre.capability.id) ?? null) : null;
  const levelOn = (levels: MissionLevel[]) => levels.some((level) => settings.levels.includes(level));
  const levelsOn = LEVEL_CHIPS.filter((chip) => levelOn(chip.levels)).length;

  const toggleLevels = (levels: MissionLevel[]) => {
    const next = levelOn(levels)
      ? settings.levels.filter((level) => !levels.includes(level))
      : [...settings.levels, ...levels];
    if (next.length > 0) {
      onChange({ ...settings, levels: MISSION_LEVELS.filter((level) => next.includes(level)) });
    }
  };
  const toggleProvider = (provider: MissionProvider) => {
    const next = settings.providers.includes(provider)
      ? settings.providers.filter((entry) => entry !== provider)
      : [...settings.providers, provider];
    if (next.length > 0) {
      onChange({ ...settings, providers: MISSION_PROVIDERS.filter((entry) => next.includes(entry)) });
    }
  };
  const parent = centre?.parent ?? null;

  return (
    <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", flexWrap: "wrap" }}>
      <Autocomplete<CentreOption>
        size="small"
        sx={{ ...PICKER_SX, flex: "0 1 470px", minWidth: 280 }}
        options={options}
        value={value}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.name}
        filterOptions={(all, state) => filterCentreOptions(all, state.inputValue)}
        getOptionDisabled={(option) => option.id === MORE_ROW}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={treeLoading && options.length === 0}
        loadingText="Reading the scope's capabilities…"
        noOptionsText="No capability in this scope"
        onChange={(_event, picked) => {
          // The centre drawn, picked again, changes nothing
          if (picked && picked.id !== (settings.centre ?? centre?.capability.id)) {
            onChange(withCentre(settings, picked.id));
          }
        }}
        renderOption={(props, option) => (
          <PickerRow key={option.id} props={props} hosts={option.hosts} depth={option.depth}>
            {option.name}
            {option.wholeArea && (
              <Box component="span" sx={{ color: "text.secondary" }}>
                {" "}
                · the whole area
              </Box>
            )}
          </PickerRow>
        )}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            placeholder="Pick or type a capability"
            inputProps={{ ...inputParams.inputProps, "aria-label": "Centre" }}
            InputProps={{
              ...inputParams.InputProps,
              startAdornment: <PickerHead>Centre</PickerHead>,
            }}
          />
        )}
      />
      <QualifierPicker
        label="Condition"
        any="any condition"
        none="none stated"
        rows={conditions}
        picked={settings.condition}
        pickedName={centre?.condition?.name ?? null}
        onPick={(id) => onChange({ ...settings, condition: id })}
      />
      <QualifierPicker
        label="Mode"
        any="any mode"
        none="none stated"
        rows={modes}
        picked={settings.mode}
        pickedName={centre?.mode?.name ?? null}
        onPick={(id) => onChange({ ...settings, mode: id })}
      />
      {parent && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => onChange({ ...settings, centre: parent.id })}
          sx={TOOL_BUTTON_SX}
        >
          Widen to {parent.name ?? parent.id}
        </Button>
      )}
      <Box role="group" aria-label="Levels" sx={{ display: "flex", gap: 0.75 }}>
        {LEVEL_CHIPS.map((chip) => (
          <ToggleChip
            key={chip.label}
            label={chip.label}
            on={levelOn(chip.levels)}
            locked={levelsOn <= 1}
            onToggle={() => toggleLevels(chip.levels)}
          />
        ))}
      </Box>
      <Box role="group" aria-label="Providers" sx={{ display: "flex", gap: 0.75 }}>
        {PROVIDER_CHIPS.map((chip) => (
          <ToggleChip
            key={chip.provider}
            label={chip.label}
            on={settings.providers.includes(chip.provider)}
            locked={settings.providers.length <= 1}
            onToggle={() => toggleProvider(chip.provider)}
          />
        ))}
      </Box>
      <Button
        variant="outlined"
        size="small"
        disabled={!drawn}
        onClick={onFit}
        sx={{ ...TOOL_BUTTON_SX, ml: "auto" }}
      >
        Fit
      </Button>
    </Box>
  );
};

export default MissionsControls;
